import { apiClient } from '../utils/api-client';
import type {
  CragRoute,
  CragRoutePayload,
  CragRoutesResponse,
  CragRouteSector,
  CragRouteGrade,
} from '../types/crag-route';

interface RouteRow {
  id: string;
  type: string;
  attributes: Omit<CragRoute, 'id'>;
}

interface ListBody {
  data: RouteRow[];
  meta: { zones: CragRouteSector[]; grades: CragRouteGrade[]; unassigned?: RouteRow[] };
}

function toRoute(row: RouteRow): CragRoute {
  return { id: row.id, ...row.attributes };
}

async function detail(response: Response, fallback: string): Promise<string> {
  const body = (await response.json().catch(() => ({}))) as { errors?: Array<{ detail?: string }> };
  return body.errors?.[0]?.detail || fallback;
}

/**
 * The crag panel's route editor, backed by `Api::Crags::RoutesController`.
 *
 * Staff-only and crag-scoped, unlike `climbsService` — which goes through
 * Elasticsearch and serves the member app. A sector here is a `Zone`, sent as
 * `zone_id` because that is what the column is called everywhere else.
 */
class CragRoutesService {
  /** A sector's routes in topo order, with the form's pickers alongside. */
  async list(cragSlug: string, sectorId?: string | null): Promise<CragRoutesResponse> {
    const query = sectorId ? `?zone_id=${encodeURIComponent(sectorId)}` : '';
    const response = await apiClient.get(`/api/crags/${cragSlug}/routes${query}`);

    if (!response.ok) throw new Error(await detail(response, 'Failed to load routes'));

    const body: ListBody = await response.json();
    return {
      routes: body.data.map(toRoute),
      unassigned: (body.meta.unassigned ?? []).map(toRoute),
      sectors: body.meta.zones,
      grades: body.meta.grades,
    };
  }

  async create(cragSlug: string, payload: CragRoutePayload): Promise<CragRoute> {
    const response = await apiClient.post(`/api/crags/${cragSlug}/routes`, {
      data: { attributes: payload },
    });

    if (!response.ok) throw new Error(await detail(response, 'Failed to create the route'));

    const body: { data: RouteRow } = await response.json();
    return toRoute(body.data);
  }

  async update(cragSlug: string, routeId: string, payload: CragRoutePayload): Promise<CragRoute> {
    const response = await apiClient.patch(`/api/crags/${cragSlug}/routes/${routeId}`, {
      data: { attributes: payload },
    });

    if (!response.ok) throw new Error(await detail(response, 'Failed to save the route'));

    const body: { data: RouteRow } = await response.json();
    return toRoute(body.data);
  }

  async remove(cragSlug: string, routeId: string): Promise<void> {
    const response = await apiClient.delete(`/api/crags/${cragSlug}/routes/${routeId}`);

    if (!response.ok) throw new Error(await detail(response, 'Failed to delete the route'));
  }

  /** The order the routes are walked past, for the whole sector at once. */
  async reorder(cragSlug: string, routeIds: string[]): Promise<void> {
    const response = await apiClient.post(`/api/crags/${cragSlug}/routes/reorder`, {
      route_ids: routeIds,
    });

    if (!response.ok) throw new Error(await detail(response, 'Failed to save the order'));
  }

  /**
   * Files routes under a sector — a handful at a time, which is what dragging a
   * selection onto a cliff asks for. Only the sector changes: the line and the
   * photo are the panel's to settle on the next save of the route itself.
   *
   * Answers how many actually moved: routes already in that sector are left
   * alone rather than refused, so the count is not always the ids sent.
   */
  async move(cragSlug: string, routeIds: string[], sectorId: string): Promise<number> {
    const response = await apiClient.post(`/api/crags/${cragSlug}/routes/move`, {
      route_ids: routeIds,
      zone_id: sectorId,
    });

    if (!response.ok) throw new Error(await detail(response, 'Failed to move the routes'));

    const body: { data: { moved: number } } = await response.json();
    return body.data.moved;
  }
}

export const cragRoutesService = new CragRoutesService();
